import { getYouTubeId, youTubeEmbedUrl } from "@/utils/constants";
import { ISocialVideos } from "@/utils/types";
import { AnimatePresence, motion } from "framer-motion";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { OutlinedButton, outlineButtonTw } from "./button";

const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), {
  ssr: false,
  loading: () => <div className="w-full h-full bg-gray-200" />,
});

/** Final fan pose per card, symmetric around the center (works for 1-5 cards). */
const poseFor = (index: number, count: number) => {
  const offset = index - (count - 1) / 2;
  return {
    rotate: offset * 8,
    // Quadratic arc: outer cards drop progressively more, like a real waaier.
    y: offset * offset * 44,
    scale: offset === 0 ? 1.05 : 1,
    zIndex: 10 - Math.round(Math.abs(offset) * 2),
  };
};

/** Identity pose for the expanded overview thumbnails. */
const FLAT_POSE = { rotate: 0, y: 0, scale: 1, zIndex: 0 };

/** Transform (in the card's local space) that centers it enlarged in the panel. */
interface IZoomTarget {
  x: number;
  y: number;
  scale: number;
  rotate: number;
}

const flyTransition = {
  type: "spring" as const,
  stiffness: 220,
  damping: 26,
};

const gridVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

const gridItemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring" as const, stiffness: 260, damping: 24 },
  },
};

export function Social(props: ISocialVideos) {
  const videos = (props.gallery?.items ?? []).filter(
    (item) => item._type === "vwc" && item.playbackId,
  );
  const panelRef = useRef<HTMLDivElement>(null);
  const thumbRefs = useRef<(HTMLDivElement | null)[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [active, setActive] = useState<number | null>(null);
  const [target, setTarget] = useState<IZoomTarget | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  // The fan and the expanded grid differ in height: spring the wrapper
  // between the two instead of letting the panel jump at the swap.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setContentHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (active === null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActive(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [active]);

  const openVideo = (
    i: number,
    el: HTMLElement | null,
    pose: ReturnType<typeof poseFor>,
  ) => {
    const panel = panelRef.current;
    if (!panel || !el || active === i) return;
    const rect = el.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();

    // Center of the card's RESTING pose (hover lifts it by 24px).
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2 + (hovered === i ? 24 : 0);

    // Center of the panel's visible portion, so the video ends up on screen.
    const visTop = Math.max(panelRect.top, 0);
    const visBottom = Math.min(panelRect.bottom, window.innerHeight);
    const tx = panelRect.left + panelRect.width / 2;
    const ty = (visTop + visBottom) / 2;

    // As large as fits within the visible panel, 9:16.
    const targetH = Math.min(
      (visBottom - visTop) * 0.85,
      panelRect.width * 0.92 * (16 / 9),
    );
    const totalScale = targetH / el.offsetHeight;

    // The zoom lives on the INNER card, nested in the rotated/scaled wrapper:
    // convert the world-space move into the wrapper's local space.
    const rad = (pose.rotate * Math.PI) / 180;
    const wx = tx - cx;
    const wy = ty - cy;
    setTarget({
      x: (Math.cos(rad) * wx + Math.sin(rad) * wy) / pose.scale,
      y: (-Math.sin(rad) * wx + Math.cos(rad) * wy) / pose.scale,
      scale: totalScale / pose.scale,
      rotate: -pose.rotate,
    });
    setActive(i);
  };

  const expand = () => {
    setActive(null);
    setHovered(null);
    setExpanded(true);
  };

  const collapse = () => {
    setActive(null);
    setHovered(null);
    setExpanded(false);
  };

  return (
    <div className="component-padding">
      <div
        ref={panelRef}
        className="relative flex flex-col items-center component-gap bg-white overflow-hidden pt-8 md:pt-16 px-8 md:px-16 text-darkGrey text-center"
      >
        <div className="flex flex-col items-center component-gap max-w-3xl">
          <p className="heading">{props.title}</p>
          {props.description && (
            <p className="opacity-80">{props.description}</p>
          )}
          {!expanded &&
            props.button?.label &&
            (videos.length > 0 ? (
              <button onClick={expand} className={outlineButtonTw}>
                {props.button.label}
              </button>
            ) : (
              props.button.link && (
                <OutlinedButton
                  link={props.button.link}
                  label={props.button.label}
                />
              )
            ))}
        </div>

        {videos.length > 0 && (
          <motion.div
            animate={contentHeight !== null ? { height: contentHeight } : undefined}
            transition={{ type: "spring", stiffness: 280, damping: 32 }}
            className="w-full"
          >
            <div ref={contentRef} className="w-full">
              <AnimatePresence mode="wait">
            {!expanded ? (
              /* The fan: on expand it drops out of the panel while fading. */
              <motion.div
                key="fan"
                exit={{ opacity: 0, y: 96 }}
                transition={{ type: "spring", stiffness: 280, damping: 30 }}
                className="flex justify-center items-start w-full pt-8 md:pt-16 h-[300px] md:h-[560px]"
              >
                {videos.map((video, i) => {
                  const pose = poseFor(i, videos.length);
                  const isActive = active === i;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 24, rotate: 0, scale: 0.9 }}
                      whileInView={{
                        opacity: 1,
                        y: pose.y,
                        rotate: pose.rotate,
                        scale: pose.scale,
                      }}
                      whileHover={
                        active === null
                          ? {
                              y: pose.y - 24,
                              scale: pose.scale + 0.04,
                              transition: {
                                type: "spring",
                                stiffness: 400,
                                damping: 28,
                              },
                            }
                          : undefined
                      }
                      viewport={{ once: true, margin: "0px 0px -80px 0px" }}
                      transition={{
                        type: "spring",
                        stiffness: 260,
                        damping: 24,
                      }}
                      onHoverStart={() => setHovered(i)}
                      onHoverEnd={() => setHovered(null)}
                      // Clicking a fan card opens the numbered overview; the
                      // muted preview keeps looping instead of unmuting midway.
                      onClick={expand}
                      style={{
                        zIndex: isActive ? 40 : hovered === i ? 30 : pose.zIndex,
                      }}
                      className={`group relative aspect-[9/16] w-[38vw] max-w-[340px] ${
                        isActive ? "" : "cursor-pointer"
                      } ${videos.length > 3 ? "-mx-8 md:-mx-12" : "-mx-4 md:-mx-6"}`}
                    >
                      {/* The zoom to panel-center lives on this inner element. */}
                      <motion.div
                        animate={
                          isActive && target
                            ? { ...target }
                            : { x: 0, y: 0, scale: 1, rotate: 0 }
                        }
                        transition={flyTransition}
                        className={`absolute inset-0 rounded-2xl md:rounded-3xl overflow-hidden ${
                          isActive ? "shadow-2xl" : "shadow-xl"
                        }`}
                      >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={`https://image.mux.com/${video.playbackId}/thumbnail.webp?time=0.1`}
                          alt={video.caption ?? ""}
                          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                        />
                        <MuxPlayer
                          streamType="on-demand"
                          playbackId={video.playbackId}
                          autoPlay
                          muted={!isActive}
                          loop
                          playsInline
                          thumbnailTime={0.1}
                          disableCookies
                          preload="auto"
                          className={`absolute inset-0 w-full h-full ${
                            isActive ? "" : "pointer-events-none"
                          }`}
                          style={
                            {
                              ["--media-object-fit" as never]: "cover",
                              ...(isActive
                                ? {}
                                : { ["--controls" as never]: "none" }),
                            } as CSSProperties
                          }
                          title={video.caption ?? ""}
                        />
                        {/* play button on hover */}
                        {!isActive && active === null && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white shadow-lg flex items-center justify-center">
                              <svg
                                viewBox="0 0 24 24"
                                className="w-6 h-6 md:w-7 md:h-7 fill-darkGrey ml-1"
                                aria-hidden="true"
                              >
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    </motion.div>
                  );
                })}
              </motion.div>
            ) : (
              /* The expanded overview: every video with its info, staggered in. */
              <motion.div
                key="grid"
                variants={gridVariants}
                initial="hidden"
                animate="show"
                className="w-full max-w-6xl flex flex-col items-center gap-8 md:gap-12 pt-4 md:pt-8 pb-8 md:pb-16"
              >
                <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 md:gap-y-10">
                  {videos.map((video, i) => {
                    const isActive = active === i;
                    const youtubeId = video.youtubeUrl
                      ? getYouTubeId(video.youtubeUrl)
                      : null;
                    return (
                      <motion.div
                        key={i}
                        variants={gridItemVariants}
                        className="flex items-start gap-4 md:gap-6 text-left border-b border-darkGrey/10 pb-6 md:pb-8"
                      >
                        <div
                          ref={(el) => {
                            thumbRefs.current[i] = el;
                          }}
                          onClick={() =>
                            openVideo(i, thumbRefs.current[i], FLAT_POSE)
                          }
                          style={{ zIndex: isActive ? 40 : undefined }}
                          className={`relative shrink-0 w-[88px] md:w-[120px] aspect-[9/16] ${
                            isActive ? "" : "cursor-pointer"
                          }`}
                        >
                          {/* Same fly-to-panel-center zoom as the fan cards. */}
                          <motion.div
                            animate={
                              isActive && target
                                ? { ...target }
                                : { x: 0, y: 0, scale: 1, rotate: 0 }
                            }
                            transition={flyTransition}
                            className={`absolute inset-0 rounded-xl md:rounded-2xl overflow-hidden ${
                              isActive ? "shadow-2xl" : "shadow-md"
                            }`}
                          >
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                              src={`https://image.mux.com/${video.playbackId}/thumbnail.webp?time=0.1`}
                              alt={video.caption ?? ""}
                              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                            />
                            {isActive &&
                              (youtubeId ? (
                                /* Plays via YouTube so the view counts there. */
                                <iframe
                                  src={youTubeEmbedUrl(youtubeId)}
                                  title={video.title ?? video.caption ?? "YouTube video"}
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  className="absolute inset-0 w-full h-full border-0 bg-black"
                                />
                              ) : (
                                <MuxPlayer
                                  streamType="on-demand"
                                  playbackId={video.playbackId}
                                  autoPlay
                                  loop
                                  playsInline
                                  thumbnailTime={0.1}
                                  disableCookies
                                  preload="auto"
                                  className="absolute inset-0 w-full h-full"
                                  style={
                                    {
                                      ["--media-object-fit" as never]: "cover",
                                    } as CSSProperties
                                  }
                                  title={video.caption ?? ""}
                                />
                              ))}
                          </motion.div>
                        </div>

                        <div className="shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-full border border-darkGrey flex items-center justify-center font-display">
                          {i + 1}
                        </div>

                        <div className="flex flex-col items-start gap-2 md:gap-3">
                          {video.title && (
                            <p className="font-display text-lg md:text-xl leading-tight">
                              {video.title}
                            </p>
                          )}
                          {video.description && (
                            <p className="opacity-80">{video.description}</p>
                          )}
                          <button
                            onClick={() =>
                              openVideo(i, thumbRefs.current[i], FLAT_POSE)
                            }
                            className={`${outlineButtonTw} mt-1 md:mt-2`}
                          >
                            Bekijk video
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                <motion.div
                  variants={gridItemVariants}
                  className="flex flex-wrap justify-center gap-4"
                >
                  <button onClick={collapse} className={outlineButtonTw}>
                    &larr; Terug
                  </button>
                  {props.expandedButton?.link && props.expandedButton?.label && (
                    <OutlinedButton
                      link={props.expandedButton.link}
                      label={props.expandedButton.label}
                    />
                  )}
                </motion.div>
              </motion.div>
            )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}

        {/* close the overview and return to the fan */}
        <AnimatePresence>
          {expanded && active === null && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={collapse}
              aria-label="Terug naar de video's"
              className="absolute top-4 right-4 z-20 w-11 h-11 rounded-full font-display text-xl text-darkGrey hover:bg-darkGrey/5 hover:scale-110 transition-all active:scale-100"
            >
              ✕
            </motion.button>
          )}
        </AnimatePresence>

        {/* dark backdrop inside the panel while a video plays */}
        <AnimatePresence>
          {active !== null && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                onClick={() => setActive(null)}
                className="absolute inset-0 z-30 bg-black/50"
              />
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                onClick={() => setActive(null)}
                aria-label="Sluiten"
                className="absolute top-4 right-4 z-40 w-10 h-10 font-display text-xl text-white hover:scale-110 transition-all active:scale-100"
              >
                ✕
              </motion.button>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
